import { useEffect, useMemo, useState } from "react"
import { shallowEqual } from "react-redux"

import notificationsAPI, { useGetUnreadNotificationCountQuery } from "@/store/notificationsAPI"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { useGetMeQuery } from "@/store/userAPI"

export function useNotificationScope() {
  const { data } = useGetMeQuery()
  return data?.user.id ? JSON.stringify([data.user.id, data.session?.tenantId ?? null]) : undefined
}
const FIRST: (string | undefined)[] = [undefined]
const endpoint = notificationsAPI.endpoints.getNotifications

/** Keep each loaded page subscribed: read mutations refresh earlier pages too. */
export function useNotificationHistory() {
  const dispatch = useAppDispatch()
  const scope = useNotificationScope()
  const [window, setWindow] = useState<{ scope?: string; cursors: (string | undefined)[] }>({ scope, cursors: FIRST })
  const cursors = window.scope === scope ? window.cursors : FIRST
  const args = useMemo(() => cursors.map((cursor) => ({ scope, cursor, limit: 20 })), [cursors, scope])
  const selectors = useMemo(() => args.map((arg) => endpoint.select(arg)), [args])
  const pages = useAppSelector((state) => selectors.map((select) => select(state)), shallowEqual)
  const unread = useGetUnreadNotificationCountQuery(scope, { skip: !scope })

  useEffect(() => {
    if (!scope) return
    const subscriptions = args.map((arg) => dispatch(endpoint.initiate(arg)))
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [args, dispatch, scope])

  // A new first/middle page may move its boundary during refresh. Retire the
  // suffix rather than retain pages whose anchors no longer follow that page.
  useEffect(() => {
    const index = pages.findIndex(
      (page, i) => i < cursors.length - 1 && page.isSuccess && page.data?.nextCursor !== cursors[i + 1],
    )
    if (index >= 0) setWindow({ scope, cursors: cursors.slice(0, index + 1) })
  }, [pages, cursors, scope])

  const items = useMemo(
    () => Array.from(new Map(pages.flatMap((page) => page.data?.items ?? []).map((item) => [item.id, item])).values()),
    [pages],
  )
  const last = pages[pages.length - 1]
  const failed = pages.findIndex((page) => page.isError)
  const isFetching = pages.some((page) => page.isLoading || page.isUninitialized)
  const refetch = () => {
    const index = failed >= 0 ? failed : pages.length - 1
    return dispatch(endpoint.initiate(args[index], { subscribe: false, forceRefetch: true }))
  }
  const restart = () => {
    setWindow({ scope, cursors: FIRST })
    if (scope)
      void dispatch(
        endpoint.initiate({ scope, limit: 20, cursor: undefined }, { subscribe: false, forceRefetch: true }),
      )
  }
  return {
    items,
    unreadCount: unread.currentData,
    isFetching,
    isLoading: isFetching && !items.length,
    isError: failed >= 0,
    error: failed >= 0 ? pages[failed].error : undefined,
    refetch,
    restart,
    nextCursor: last?.data?.nextCursor ?? null,
    loadMore: () => {
      const next = last?.data?.nextCursor
      if (next && !isFetching && !cursors.includes(next)) setWindow({ scope, cursors: [...cursors, next] })
    },
  }
}
